import { nextTick, onBeforeUnmount, Ref, ref, watch } from 'vue'

interface UseSequentialPlaybackParams {
  total: Ref<number>
  stepIntervalMs: Ref<number>
  isPlaying: Ref<boolean>
  isReset: Ref<boolean>
  onComplete: () => void
  scrollContainer?: Ref<HTMLElement | null>
}

interface UseSequentialPlaybackResult {
  visibleCount: Ref<number>
}

export const useSequentialPlayback = (params: UseSequentialPlaybackParams): UseSequentialPlaybackResult => {
  const { total, stepIntervalMs, isPlaying, isReset, onComplete, scrollContainer } = params
  const visibleCount = ref(0)
  let timerId: ReturnType<typeof setInterval> | null = null

  const stop = () => {
    if (timerId !== null) {
      clearInterval(timerId)
      timerId = null
    }
  }

  const start = () => {
    stop()

    if (visibleCount.value >= total.value) return

    timerId = setInterval(() => {
      if (visibleCount.value < total.value) {
        visibleCount.value++
      }

      if (visibleCount.value >= total.value) {
        stop()
        onComplete()
      }
    }, stepIntervalMs.value)
  }

  watch(
    isPlaying,
    playing => {
      if (playing) {
        start()
      } else {
        stop()
      }
    },
    { immediate: true }
  )

  watch(isReset, reset => {
    if (!reset) return

    visibleCount.value = 0
    if (isPlaying.value) {
      start()
    }
  })

  watch(stepIntervalMs, () => {
    if (isPlaying.value && timerId !== null) {
      start()
    }
  })

  watch(total, newTotal => {
    if (visibleCount.value > newTotal) {
      visibleCount.value = newTotal
    }
  })

  watch(visibleCount, async (newCount, oldCount) => {
    if (scrollContainer === undefined || newCount <= oldCount) return

    await nextTick()
    const container = scrollContainer.value
    if (container === null) return

    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
  })

  onBeforeUnmount(stop)

  return { visibleCount }
}
